import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { EntityMatch, registerLexicalTextEntity } from '@lexical/text';
import { mergeRegister } from '@lexical/utils';
import {
  $applyNodeReplacement,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  TextNode,
} from 'lexical';
import { useEffect } from 'react';
import { BUILTINS } from '../../formula-engine';
import { isInsideStringLiteral } from './StringLiteralPlugin';

export const FUNCTION_NAME_CLASS = 'formula-function-name';

/**
 * Pure syntax highlighting for function names: a text entity (like
 * StringLiteralNode), so the name stays ordinary editable text and converts
 * back to plain text the moment it no longer reads as a call.
 */
export class FunctionNameNode extends TextNode {
  static getType(): string {
    return 'formula-function';
  }

  static clone(node: FunctionNameNode): FunctionNameNode {
    return new FunctionNameNode(node.__text, node.__key);
  }

  constructor(text: string, key?: NodeKey) {
    super(text, key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.className = FUNCTION_NAME_CLASS;
    return dom;
  }

  static importJSON(serializedNode: SerializedTextNode): FunctionNameNode {
    return $createFunctionNameNode(serializedNode.text).updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedTextNode {
    return { ...super.exportJSON(), type: FunctionNameNode.getType() };
  }

  /**
   * False, against `registerLexicalTextEntity`'s advice. Its two adjacency
   * rules — never start an entity where another one ends, and unwrap a pair
   * that ends up side by side — assume hashtag-like entities that plain text
   * always separates. Highlighted formula tokens legitimately touch ("IF(AND(",
   * "ABS(1"), and under those rules whichever of the pair the transforms
   * reached second lost its colour.
   */
  isTextEntity(): false {
    return false;
  }
}

export function $createFunctionNameNode(text: string): FunctionNameNode {
  return $applyNodeReplacement(new FunctionNameNode(text));
}

export function $isFunctionNameNode(node: LexicalNode | null | undefined): node is FunctionNameNode {
  return node instanceof FunctionNameNode;
}

const CALL_NAME = /([A-Za-z_][A-Za-z0-9_]*)\(/g;

/**
 * First known function name written as a call ("REDUCE(", any case). The "("
 * is part of the entity, so the match is self-contained in the node's own
 * text (the revert check re-runs it there) and a half-typed name stays plain
 * for the function typeahead. Names inside a string literal are text.
 */
export function getFunctionNameMatch(text: string): EntityMatch | null {
  CALL_NAME.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = CALL_NAME.exec(text))) {
    if (BUILTINS.has(match[1]) && !isInsideStringLiteral(text.slice(0, match.index))) {
      return { start: match.index, end: match.index + match[0].length };
    }
  }
  return null;
}

export function FunctionNamePlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return mergeRegister(
      ...registerLexicalTextEntity(editor, getFunctionNameMatch, FunctionNameNode, (textNode) =>
        $createFunctionNameNode(textNode.getTextContent())
      )
    );
  }, [editor]);
  return null;
}
